import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import TranscriptionService from './components/TranscriptionService';
import FileManager from './components/FileManager';
import SecretsManager from './components/SecretsManager';
import DNSManager from './components/DNSManager';
import ProjectBackup from './components/ProjectBackup';
import AutomatedTesting from './components/AutomatedTesting';
import GitHubBrowser from './components/GitHubBrowser';
import GitHubTokenManager from './components/GitHubTokenManager';
import ProjectsUnified from './components/ProjectsUnified';
import SecurityMonitor from './components/SecurityMonitor';
import SecurityAgentDashboard from './components/SecurityAgentDashboard';
import PromptLibrary from './components/PromptLibrary';
import ErrorMonitor from './components/ErrorMonitor';
import DatabaseViewer from './components/DatabaseViewer';
import TrackingSystem from './components/TrackingSystem';
import E2ETestingDashboard from './components/E2ETestingDashboard';
import StatusMatrix from './components/StatusMatrix';

function App() {
  // Authentication state - MUST be before any conditional returns
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Use proxy paths for production (HTTPS) or direct ports for local development
  const useProxy = window.location.protocol === 'https:';

  // Services Configuration
  const [services] = useState({
    // Core Services
    services: [
      {
        id: 'transcription',
        name: 'Transcription',
        description: 'AI-powered speech-to-text using OpenAI Whisper',
        status: 'checking',
        url: useProxy ? '/api/transcription' : `http://${window.location.hostname}:8009`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8009',
          hostname: 'localhost',
          location: 'Local Server',
          model: null,
          version: null
        }
      },
      {
        id: 'storage',
        name: 'Storage',
        description: 'S3-based file management with virtual folder structure',
        status: 'checking',
        url: useProxy ? '/api/transcription' : `http://${window.location.hostname}:8009`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        healthEndpoint: '/api/storage/health',
        server: {
          ip: 'localhost',
          port: '8009',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'S3 Storage',
          version: null
        }
      },
      {
        id: 'secrets',
        name: 'Secrets',
        description: 'Simple file-based secrets management with Fernet encryption',
        status: 'checking',
        url: useProxy ? '/api/vault' : `http://${window.location.hostname}:8000`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8007',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'Simple Vault',
          version: null
        }
      }
    ],
    // Project Tools
    projects: [
      {
        id: 'project-management',
        name: 'Project Management',
        description: 'Comprehensive project management with documentation, issue tracking, and service integration',
        status: 'checking',
        url: useProxy ? '/api/projects' : `http://${window.location.hostname}:8010`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8010',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'Project Management',
          version: null
        }
      },
      {
        id: 'backup',
        name: 'Backup',
        description: 'Automated backup service for GitHub repositories and PostgreSQL databases',
        status: 'checking',
        url: useProxy ? '/api/backup' : `http://${window.location.hostname}:8007`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8009',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'Backup Manager',
          version: null
        }
      },
      {
        id: 'testing',
        name: 'Testing',
        description: 'Professional Django/React client testing with security analysis and reporting',
        status: 'checking',
        url: useProxy ? '/api/testing' : `http://${window.location.hostname}:8011`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8011',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'Testing Service',
          version: null
        }
      },
      {
        id: 'github',
        name: 'GitHub',
        description: 'GitHub repository discovery and auto-configuration for testing and backup services',
        status: 'checking',
        url: useProxy ? '/api/testing' : `http://${window.location.hostname}:8013`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8013',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'GitHub Integration',
          version: null
        }
      },
      {
        id: 'dns',
        name: 'DNS Manager',
        description: 'Manage DNS records via DNSimple API integration',
        status: 'checking',
        url: useProxy ? '/api/dns' : `http://${window.location.hostname}:8011`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8011',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'DNSimple',
          version: null
        }
      },
      {
        id: 'errors',
        name: 'Error Monitor',
        description: 'Monitor and track errors from Sentry for all projects',
        status: 'checking',
        url: useProxy ? '/api/errors' : `http://${window.location.hostname}:8014`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8014',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'Sentry',
          version: null
        }
      },
      {
        id: 'security-agent',
        name: 'Security Agent',
        description: 'Autonomous threat detection and response with approval workflow',
        status: 'checking',
        url: useProxy ? '/api/security-agent' : `http://${window.location.hostname}:8020`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8020',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'Security Agent',
          version: null
        }
      },
      {
        id: 'e2e-testing',
        name: 'E2E Testing',
        description: 'Automated E2E test generation, execution, and Sentry error correlation',
        status: 'checking',
        url: useProxy ? '/api/e2e' : `http://${window.location.hostname}:8021`,
        apiKey: 'inDVbj0JioxlN9vHxTRmnw25NEqOnjazOmUiFftTs0Q',
        server: {
          ip: 'localhost',
          port: '8021',
          hostname: 'localhost',
          location: 'Local Server',
          model: 'E2E Testing Agent',
          version: null
        }
      }
    ]
  });

  const [serviceStatuses, setServiceStatuses] = useState({});

  const [activeMainTab, setActiveMainTab] = useState('infra');
  const [activeServiceTab, setActiveServiceTab] = useState('transcription');
  const [activeProjectTab, setActiveProjectTab] = useState('project-management');
  const [activeSecurityTab, setActiveSecurityTab] = useState('monitor');
  const [activeQATab, setActiveQATab] = useState('e2e');

  const getAllServices = () => [...services.services, ...services.projects];

  const checkServiceHealth = async (service) => {
    try {
      // Use custom health endpoint if specified, otherwise default to /health
      const healthEndpoint = service.healthEndpoint || '/health';

      console.log(`Checking health for ${service.name} at ${service.url}${healthEndpoint}`);

      // Create a promise that will timeout after 30 seconds
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Connection timeout')), 30000)
      );

      const fetchPromise = fetch(`${service.url}${healthEndpoint}`);
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      console.log(`Health check response for ${service.name}:`, response.status, response.ok);

      if (response.ok) {
        const healthData = await response.json();

        // Check if service indicates it's still initializing
        const isInitializing = healthData.status === 'initializing' ||
                              healthData.message?.includes('async initialization') ||
                              healthData.message?.includes('model loading in background') ||
                              healthData.message?.includes('background initialization');

        return {
          status: isInitializing ? 'initializing' : 'healthy',
          serverInfo: {
            model: healthData.whisper_model || healthData.s3_bucket || healthData.service || service.server.model,
            version: healthData.version || '1.0.0',
            timestamp: healthData.timestamp || new Date().toISOString(),
            initMessage: isInitializing ? (healthData.message || 'Service is initializing...') : null
          }
        };
      } else if (response.status === 403) {
        return { status: 'ip-restricted', serverInfo: null };
      } else if (response.status === 502 || response.status === 503) {
        // Service is starting up or temporarily unavailable
        return { status: 'starting', serverInfo: null };
      } else {
        return { status: 'unhealthy', serverInfo: null };
      }
    } catch (error) {
      console.error(`Health check failed for ${service.name}:`, error);

      // Distinguish between different types of connection errors
      if (error.message === 'Connection timeout') {
        // Timeout after 30s - service might be processing long operation
        console.warn(`${service.name} health check timed out after 30 seconds`);
        return { status: 'unhealthy', serverInfo: null };
      } else if (error.name === 'TypeError' || error.message.includes('Failed to fetch')) {
        // Network/CORS error - service might be restarting or unreachable
        return { status: 'offline', serverInfo: null };
      } else if (error.code === 'ECONNREFUSED') {
        // Connection refused - service is definitely offline
        return { status: 'offline', serverInfo: null };
      } else {
        // Unknown error - assume offline
        console.warn(`${service.name} health check failed:`, error.message);
        return { status: 'offline', serverInfo: null };
      }
    }
  };

  useEffect(() => {
    const checkAllServices = async () => {
      const allServices = getAllServices();
      const updatedStatuses = {};

      for (const service of allServices) {
        const healthResult = await checkServiceHealth(service);
        updatedStatuses[service.id] = {
          ...service,
          status: healthResult.status,
          server: {
            ...service.server,
            model: healthResult.serverInfo?.model || service.server.model,
            version: healthResult.serverInfo?.version || service.server.version,
            lastChecked: healthResult.serverInfo?.timestamp || new Date().toISOString()
          }
        };
      }

      setServiceStatuses(updatedStatuses);
    };

    checkAllServices();
    const interval = setInterval(checkAllServices, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'initializing': return 'text-gray-600 bg-gray-100';
      case 'starting': return 'text-gray-600 bg-gray-100';
      case 'unhealthy': return 'text-yellow-600 bg-yellow-100';
      case 'offline': return 'text-red-600 bg-red-100';
      case 'ip-restricted': return 'text-orange-600 bg-orange-100';
      case 'checking': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'healthy': return 'Online';
      case 'initializing': return 'Initializing...';
      case 'starting': return 'Starting...';
      case 'unhealthy': return 'Issues';
      case 'offline': return 'Offline';
      case 'ip-restricted': return 'IP Restricted';
      case 'checking': return 'Checking...';
      default: return 'Unknown';
    }
  };

  const getStatusMessage = (status, serviceName) => {
    switch (status) {
      case 'checking':
        return `Checking if the ${serviceName.toLowerCase()} service is available...`;
      case 'starting':
        return `The ${serviceName.toLowerCase()} service is starting up. This may take a few moments.`;
      case 'initializing':
        return `The ${serviceName.toLowerCase()} service is loading in the background. You can try using it now.`;
      case 'offline':
        return `The ${serviceName.toLowerCase()} service is currently unavailable. Please check the service status.`;
      case 'unhealthy':
        return `The ${serviceName.toLowerCase()} service is experiencing issues. Please try again later.`;
      case 'ip-restricted':
        return `Your IP address is not authorized to access this service.`;
      default:
        return `Connecting to ${serviceName.toLowerCase()} service...`;
    }
  };

  // Loading state component
  const ServiceLoadingState = ({ status, serviceName }) => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className={`rounded-full p-4 ${
        status === 'checking' || status === 'starting' || status === 'initializing'
          ? 'bg-gray-50'
          : 'bg-red-50'
      }`}>
        <div className={`text-4xl ${
          status === 'checking' || status === 'starting' || status === 'initializing'
            ? 'text-gray-500'
            : 'text-red-500'
        }`}>
          {status === 'checking' || status === 'starting' || status === 'initializing' ? <i className="fas fa-hourglass-half"></i> : <i className="fas fa-exclamation-triangle"></i>}
        </div>
      </div>
      <div className="text-center mt-6 max-w-md">
        <h3 className={`text-lg font-semibold ${
          status === 'checking' || status === 'starting' || status === 'initializing'
            ? 'text-gray-900'
            : 'text-red-900'
        }`}>
          {status === 'checking' ? 'Connecting...' :
           status === 'starting' ? 'Service Starting...' :
           status === 'initializing' ? 'Service Initializing...' :
           'Service Offline'}
        </h3>
        <p className={`text-sm mt-1 ${
          status === 'checking' || status === 'starting' || status === 'initializing'
            ? 'text-gray-700'
            : 'text-red-700'
        }`}>
          {getStatusMessage(status, serviceName)}
        </p>
      </div>
    </div>
  );

  // Authentication handlers
  const handleLogin = (authToken) => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('username');
    setIsAuthenticated(false);
  };

  // Check for existing auth on mount
  useEffect(() => {
    const authToken = sessionStorage.getItem('authToken');
    if (authToken) {
      setIsAuthenticated(true);
    }
    setIsCheckingAuth(false);
  }, []);

  // Show loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFC65D] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-3 sm:py-6 gap-3 sm:gap-0">
            <div className="flex items-center space-x-4 w-full sm:w-auto">
              <img
                src="https://digimint.co/_next/image?url=%2Flogo_light.png&w=256&q=75"
                alt="Digimint Logo"
                className="h-5 sm:h-6 md:h-8 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="flex-1 sm:hidden text-right">
                <div className="text-xs font-medium text-gray-900">
                  {Object.values(serviceStatuses).filter(s => s.status === 'healthy').length}/{getAllServices().length} online
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className="text-left sm:text-right w-full sm:w-auto hidden sm:block">
                <div className="text-xs sm:text-sm font-medium text-gray-900">
                  {Object.values(serviceStatuses).filter(s => s.status === 'healthy').length} / {getAllServices().length} services online
                  {Object.values(serviceStatuses).filter(s => s.status === 'ip-restricted').length > 0 && (
                    <span className="text-xs text-orange-600 ml-2 sm:block sm:ml-0 sm:mt-1">
                      {Object.values(serviceStatuses).filter(s => s.status === 'ip-restricted').length} IP restricted
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 hidden md:block">
                  Dashboard Server: {window.location.hostname}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 w-full sm:w-auto justify-center"
                title="Logout"
              >
                <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Main Tab Navigation - Services vs Projects */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 sm:mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex px-2 sm:px-3 md:px-6 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveMainTab('infra')}
                className={`py-2.5 sm:py-3 md:py-4 px-2 sm:px-3 md:px-6 border-b-2 font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0 ${
                  activeMainTab === 'infra'
                    ? 'border-[#FFC65D] text-[#FFC65D]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-th mr-1 sm:mr-2" style={{ color: '#000000' }}></i>
                <span className="hidden xs:inline">Infrastructure</span>
              </button>
              <button
                onClick={() => setActiveMainTab('projects')}
                className={`py-2.5 sm:py-3 md:py-4 px-2 sm:px-3 md:px-6 border-b-2 font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0 ${
                  activeMainTab === 'projects'
                    ? 'border-[#FFC65D] text-[#FFC65D]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-chart-bar mr-1 sm:mr-2" style={{ color: '#000000' }}></i>
                <span className="hidden xs:inline">Projects</span>
              </button>
              <button
                onClick={() => setActiveMainTab('services')}
                className={`py-2.5 sm:py-3 md:py-4 px-2 sm:px-3 md:px-6 border-b-2 font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0 ${
                  activeMainTab === 'services'
                    ? 'border-[#FFC65D] text-[#FFC65D]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-cog mr-1 sm:mr-2" style={{ color: '#000000' }}></i>
                <span className="hidden xs:inline">Services</span>
              </button>
              <button
                onClick={() => setActiveMainTab('security')}
                className={`py-2.5 sm:py-3 md:py-4 px-2 sm:px-3 md:px-6 border-b-2 font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0 ${
                  activeMainTab === 'security'
                    ? 'border-[#FFC65D] text-[#FFC65D]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-shield-alt mr-1 sm:mr-2" style={{ color: '#000000' }}></i>
                <span className="hidden xs:inline">Security</span>
              </button>
              <button
                onClick={() => setActiveMainTab('qa')}
                className={`py-2.5 sm:py-3 md:py-4 px-2 sm:px-3 md:px-6 border-b-2 font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0 ${
                  activeMainTab === 'qa'
                    ? 'border-[#FFC65D] text-[#FFC65D]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-flask mr-1 sm:mr-2" style={{ color: '#000000' }}></i>
                <span className="hidden xs:inline">QA</span>
              </button>
              <button
                onClick={() => setActiveMainTab('prompts')}
                className={`py-2.5 sm:py-3 md:py-4 px-2 sm:px-3 md:px-6 border-b-2 font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0 ${
                  activeMainTab === 'prompts'
                    ? 'border-[#FFC65D] text-[#FFC65D]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-lightbulb mr-1 sm:mr-2" style={{ color: '#000000' }}></i>
                <span className="hidden xs:inline">Prompts</span>
              </button>
              <button
                onClick={() => setActiveMainTab('tracking')}
                className={`py-2.5 sm:py-3 md:py-4 px-2 sm:px-3 md:px-6 border-b-2 font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0 ${
                  activeMainTab === 'tracking'
                    ? 'border-[#FFC65D] text-[#FFC65D]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="fas fa-tasks mr-1 sm:mr-2" style={{ color: '#000000' }}></i>
                <span className="hidden xs:inline">Tracking</span>
              </button>
            </nav>
          </div>

          {/* Sub-tab Navigation */}
          <div className="bg-gray-50 border-b border-gray-200">
            <nav className="-mb-px flex space-x-1 sm:space-x-3 md:space-x-6 px-2 sm:px-3 md:px-6 overflow-x-auto scrollbar-hide">
              {activeMainTab === 'services' && (
                <>
                  {services.services.map((service) => {
                    const status = serviceStatuses[service.id]?.status || 'checking';
                    return (
                      <button
                        key={service.id}
                        onClick={() => setActiveServiceTab(service.id)}
                        className={`py-2.5 sm:py-3 px-1.5 sm:px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                          activeServiceTab === service.id
                            ? 'border-[#FFC65D] text-[#FFC65D]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span>{service.name}</span>
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)} hidden xs:inline`}>
                          {getStatusLabel(status)}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
              {activeMainTab === 'projects' && (
                <>
                  {services.projects.filter(s => s.id !== 'security-agent' && s.id !== 'e2e-testing').map((service) => {
                    const status = serviceStatuses[service.id]?.status || 'checking';
                    return (
                      <button
                        key={service.id}
                        onClick={() => setActiveProjectTab(service.id)}
                        className={`py-2.5 sm:py-3 px-1.5 sm:px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                          activeProjectTab === service.id
                            ? 'border-[#FFC65D] text-[#FFC65D]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span>{service.name}</span>
                        <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)} hidden xs:inline`}>
                          {getStatusLabel(status)}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
              {activeMainTab === 'security' && (
                <>
                  <button
                    onClick={() => setActiveSecurityTab('monitor')}
                    className={`py-2.5 sm:py-3 px-1.5 sm:px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                      activeSecurityTab === 'monitor'
                        ? 'border-[#FFC65D] text-[#FFC65D]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className="fas fa-eye mr-1"></i>
                    <span>Monitor</span>
                  </button>
                  <button
                    onClick={() => setActiveSecurityTab('agent')}
                    className={`py-2.5 sm:py-3 px-1.5 sm:px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                      activeSecurityTab === 'agent'
                        ? 'border-[#FFC65D] text-[#FFC65D]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className="fas fa-robot mr-1"></i>
                    <span>Agent</span>
                    <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${
                      serviceStatuses['security-agent']?.status === 'healthy'
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-600'
                    } hidden xs:inline`}>
                      {getStatusLabel(serviceStatuses['security-agent']?.status || 'checking')}
                    </span>
                  </button>
                </>
              )}
              {activeMainTab === 'qa' && (
                <>
                  <button
                    onClick={() => setActiveQATab('e2e')}
                    className={`py-2.5 sm:py-3 px-1.5 sm:px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${
                      activeQATab === 'e2e'
                        ? 'border-[#FFC65D] text-[#FFC65D]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <i className="fas fa-vial mr-1"></i>
                    <span>E2E Testing</span>
                    <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${
                      serviceStatuses['e2e-testing']?.status === 'healthy'
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-600'
                    } hidden xs:inline`}>
                      {getStatusLabel(serviceStatuses['e2e-testing']?.status || 'checking')}
                    </span>
                  </button>
                </>
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Infrastructure Content */}
          {activeMainTab === 'infra' && (
            <StatusMatrix />
          )}

          {/* Services Content */}
          {activeMainTab === 'services' && (
            <>
              {services.services.map((service) => {
                const serviceWithStatus = serviceStatuses[service.id] || service;
                return activeServiceTab === service.id ? (
                  <div key={`service-${service.id}`}>
                    <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">{service.name}</h2>
                        <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(serviceWithStatus.status)} self-start sm:self-auto`}>
                          {getStatusLabel(serviceWithStatus.status)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-xs sm:text-sm mt-1">{service.description}</p>
                    </div>
                    <div className={service.id === 'storage' ? '' : 'p-3 sm:p-4 md:p-6'}>
                      {service.id === 'transcription' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <TranscriptionService
                            service={serviceWithStatus}
                            isOnline={true}
                            status={serviceWithStatus.status}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                      {service.id === 'storage' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <div className="h-[500px] sm:h-[600px]">
                            <FileManager
                              service={serviceWithStatus}
                              isOnline={true}
                            />
                          </div>
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                      {service.id === 'secrets' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <SecretsManager
                            service={serviceWithStatus}
                            isOnline={true}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                    </div>
                  </div>
                ) : null;
              })}
            </>
          )}

          {/* Projects Content */}
          {activeMainTab === 'projects' && (
            <>
              {services.projects.filter(s => s.id !== 'security-agent' && s.id !== 'e2e-testing').map((service) => {
                const serviceWithStatus = serviceStatuses[service.id] || service;
                return activeProjectTab === service.id ? (
                  <div key={`project-${service.id}`}>
                    <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">{service.name}</h2>
                        <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(serviceWithStatus.status)} self-start sm:self-auto`}>
                          {getStatusLabel(serviceWithStatus.status)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-xs sm:text-sm mt-1">{service.description}</p>
                    </div>
                    <div className="p-3 sm:p-4 md:p-6">
                      {service.id === 'backup' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <ProjectBackup
                            service={serviceWithStatus}
                            isOnline={true}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                      {service.id === 'testing' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <AutomatedTesting
                            service={serviceWithStatus}
                            isOnline={true}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                      {service.id === 'github' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <GitHubBrowser
                            service={serviceWithStatus}
                            isOnline={true}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                      {service.id === 'project-management' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <ProjectsUnified
                            service={serviceWithStatus}
                            isOnline={true}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                      {service.id === 'dns' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <DNSManager
                            service={serviceWithStatus}
                            isOnline={true}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                      {service.id === 'errors' && (
                        serviceWithStatus.status === 'healthy' ? (
                          <ErrorMonitor
                            service={serviceWithStatus}
                            isOnline={true}
                          />
                        ) : (
                          <ServiceLoadingState status={serviceWithStatus.status} serviceName={service.name} />
                        )
                      )}
                    </div>
                  </div>
                ) : null;
              })}
            </>
          )}

          {/* Security Content */}
          {activeMainTab === 'security' && (
            <div className="p-3 sm:p-4 md:p-6">
              {activeSecurityTab === 'monitor' && <SecurityMonitor />}
              {activeSecurityTab === 'agent' && <SecurityAgentDashboard />}
            </div>
          )}

          {/* QA Content */}
          {activeMainTab === 'qa' && (
            <div className="p-3 sm:p-4 md:p-6">
              {activeQATab === 'e2e' && <E2ETestingDashboard />}
            </div>
          )}

          {/* Prompts Content */}
          {activeMainTab === 'prompts' && (
            <PromptLibrary
              secretsService={serviceStatuses['secrets'] || services.services.find(s => s.id === 'secrets')}
            />
          )}

          {/* Tracking Content */}
          {activeMainTab === 'tracking' && (
            <TrackingSystem />
          )}
        </div>

      </main>
    </div>
  );
}

export default App;
